import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Building2, Plus } from "lucide-react";
import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthProvider";
import type { OrgInfo, UserOrgsResponse } from "@/api/types";
import logoUrl from "@/assets/logo.svg";
import { Card, CardContent, CardDescription, CardHeader } from "@/components/ui/card";

export function ChooseOrgPage() {
  const { t } = useTranslation();
  const { user, refreshAuth } = useAuth();
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState<OrgInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newOrgName, setNewOrgName] = useState("");
  const [createError, setCreateError] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const loadOrgs = () => {
    api<UserOrgsResponse>("/api/v2/user/orgs")
      .then((data) => {
        if (data.orgs.length <= 1 && !creating) {
          navigate("/", { replace: true });
          return;
        }
        setOrgs(data.orgs);
      })
      .catch(() => navigate("/login", { replace: true }))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadOrgs();
    // Intentionally excludes `loadOrgs` from deps: it closes over `creating`,
    // and adding it would re-run the fetch on every "crear org" toggle,
    // re-triggering the `data.orgs.length <= 1 && !creating` redirect logic
    // on mount only, not on every toggle (RAISE-12980 -- UX decision, not an
    // oversight).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const handleSelect = async (orgId: string) => {
    if (switching) return;
    setSwitching(true);
    try {
      await api("/api/v2/auth/switch-org", {
        method: "POST",
        body: JSON.stringify({ org_id: orgId }),
      });
      await refreshAuth();
      navigate("/", { replace: true });
    } catch {
      setSwitching(false);
    }
  };

  const handleCreateOrg = async () => {
    if (!user || !newOrgName.trim() || newOrgName.length < 2) return;
    setCreateError("");
    setSwitching(true);
    try {
      const result = await api<{ org_id: string }>("/api/v2/auth/signup", {
        method: "POST",
        body: JSON.stringify({
          email: user.email,
          org_name: newOrgName.trim(),
          name: user.name,
        }),
      });
      await api("/api/v2/auth/switch-org", {
        method: "POST",
        body: JSON.stringify({ org_id: result.org_id }),
      });
      await refreshAuth();
      navigate("/", { replace: true });
    } catch {
      setCreateError(t("chooseOrg.createFailed"));
      setSwitching(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-muted-foreground">{t("chooseOrg.loading")}</p>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-1">
            <img src={logoUrl} alt="RaiSE" className="h-8 invert dark:invert-0" />
          </div>
          <h1 className="text-lg font-semibold">{t("chooseOrg.title")}</h1>
          <CardDescription>{t("chooseOrg.subtitle")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Org list — bounded + scrollable */}
          <div className="scrollbar-hide max-h-[50vh] space-y-2 overflow-y-auto">
            {orgs.map((org) => (
              <button
                key={org.org_id}
                disabled={switching}
                onClick={() => handleSelect(org.org_id)}
                className="flex w-full items-center gap-3 rounded-lg border p-4 text-left transition-colors hover:bg-accent disabled:opacity-50"
              >
                <Building2 className="h-5 w-5 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <p className="font-medium truncate">{org.org_name}</p>
                  <p className="text-xs text-muted-foreground capitalize">
                    {org.role}
                  </p>
                </div>
              </button>
            ))}
          </div>
          {/* Create-org block — outside scroll, always visible */}
          {!creating ? (
            <button
              onClick={() => {
                setCreating(true);
                setTimeout(() => inputRef.current?.focus(), 0);
              }}
              disabled={switching}
              className="flex w-full items-center gap-3 rounded-lg border border-dashed p-4 text-left transition-colors hover:bg-accent disabled:opacity-50"
            >
              <Plus className="h-5 w-5 shrink-0 text-muted-foreground" />
              <p className="font-medium text-muted-foreground">
                {t("chooseOrg.createNew")}
              </p>
            </button>
          ) : (
            <div className="rounded-lg border border-dashed p-4 space-y-3">
              <input
                ref={inputRef}
                type="text"
                placeholder={t("chooseOrg.orgNamePlaceholder")}
                value={newOrgName}
                onChange={(e) => setNewOrgName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleCreateOrg();
                  if (e.key === "Escape") {
                    setCreating(false);
                    setNewOrgName("");
                    setCreateError("");
                  }
                }}
                disabled={switching}
                className="w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
              {createError && (
                <p className="text-xs text-destructive">{createError}</p>
              )}
              <div className="flex gap-2">
                <button
                  onClick={handleCreateOrg}
                  disabled={switching || newOrgName.trim().length < 2}
                  className="flex-1 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                >
                  {switching ? t("chooseOrg.creating") : t("chooseOrg.create")}
                </button>
                <button
                  onClick={() => {
                    setCreating(false);
                    setNewOrgName("");
                    setCreateError("");
                  }}
                  disabled={switching}
                  className="rounded-md border px-3 py-2 text-sm hover:bg-accent disabled:opacity-50"
                >
                  {t("chooseOrg.cancel")}
                </button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
